import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { useNavigate, useParams } from 'react-router';
import useNotifications from '../../hooks/useNotifications/useNotifications';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormFieldValue } from '../forms/FormFieldValue';
import PageContainer from '../layout/PageContainer';
import { useLazyQuery, useMutation } from '@apollo/client/react';
import CustomerForm, { CustomerFormState } from './CustomerForm';
import { type Customer, validate as validateCustomer } from '../../models/Customer';
import { GET_CUSTOMER_BY_ID_QUERY, GET_CUSTOMERS_QUERY } from '../../queries/CustomerQueries';
import { UPDATE_CUSTOMER_MUTATION } from '../../mutations/CustomerMutations';

function CustomerEditForm({
  initialValues,
  onSubmit,
}: {
  initialValues: Partial<CustomerFormState['values']>;
  onSubmit: (id: number, formValues: Partial<CustomerFormState['values']>) => Promise<boolean>;
}) {
  const { customerId } = useParams();
  const navigate = useNavigate();

  const notifications = useNotifications();

  const [formState, setFormState] = useState<CustomerFormState>(() => ({
    values: initialValues,
    errors: {},
  }));

  const formValues = formState.values;
  const formErrors = formState.errors;

  const setFormValues = useCallback(
    (newFormValues: Partial<CustomerFormState['values']>) => {
      setFormState((previousState) => ({
        ...previousState,
        values: newFormValues,
      }));
    },
    [],
  );

  const setFormErrors = useCallback(
    (newFormErrors: Partial<CustomerFormState['errors']>) => {
      setFormState((previousState) => ({
        ...previousState,
        errors: newFormErrors,
      }));
    },
    [],
  );

  const handleFormFieldChange = useCallback(
    (name: keyof CustomerFormState['values'], value: FormFieldValue) => {
      const validateField = async (values: Partial<CustomerFormState['values']>) => {
        const { issues } = validateCustomer(values);
        setFormErrors({
          ...formErrors,
          [name]: issues?.find((issue) => issue.path?.[0] === name)?.message,
        });
      };

      const newFormValues = { ...formValues, [name]: value };

      setFormValues(newFormValues);
      validateField(newFormValues);
    },
    [formValues, formErrors, setFormErrors, setFormValues],
  );

  const handleFormReset = useCallback(() => {
    setFormValues(initialValues);
  }, [initialValues, setFormValues]);

  const handleFormSubmit = useCallback(async () => {
    const { issues } = validateCustomer(formValues);

    if (issues && issues.length > 0) {
      setFormErrors(
        Object.fromEntries(issues.map((issue) => [issue.path?.[0], issue.message])),
      );
      return;
    }

    setFormErrors({});

    const id = +(customerId || '0');

    if (id > 0) {
      try {
        const success = await onSubmit(id, formValues);

        if (success) {
          notifications.show('Customer edited successfully.', {
            severity: 'success',
            autoHideDuration: 3000,
          });

          navigate(`/dashboard/customers/${id}`);
        } else {
          notifications.show(
            'Failed to edit customer. Reason: Unknown.',
            {
              severity: 'error',
              autoHideDuration: 3000,
            },
          );
        }
      } catch (editError) {
        notifications.show(
          `Failed to edit customer. Reason: ${(editError as Error).message}`,
          {
            severity: 'error',
            autoHideDuration: 3000,
          },
        );
        throw editError;
      }
    }
  }, [formValues, navigate, notifications, onSubmit, setFormErrors, customerId]);

  return (
    <CustomerForm
      formState={formState}
      onFieldChange={handleFormFieldChange}
      onSubmit={handleFormSubmit}
      onReset={handleFormReset}
      submitButtonLabel="Save"
      backButtonPath={`/dashboard/customers/${customerId}`}
    />
  );
}

export default function CustomerEdit() {
  const { customerId } = useParams();

  const [getCustomerById] = useLazyQuery(
    GET_CUSTOMER_BY_ID_QUERY);

  const [updateCustomer] = useMutation(
    UPDATE_CUSTOMER_MUTATION, {
    refetchQueries: [{ query: GET_CUSTOMERS_QUERY }, { query: GET_CUSTOMER_BY_ID_QUERY, variables: { id: +(customerId || '0') } }],
  });

  const [customer, setCustomer] = useState<Customer | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const loadData = useCallback(async () => {
    setError(null);

    const id = +(customerId || '0');

    if (id > 0) {
      setIsLoading(true);

      try {
        const { data, error: getCustomerByIdError } = await getCustomerById({ variables: { id } });

        setIsLoading(false);
        setError(null);

        if (getCustomerByIdError) {
          setError(getCustomerByIdError);
        }

        if (data?.customer) {
          setCustomer(data.customer);
        } else {
          setError({ message: 'No customer found' } as Error);
        }

      } catch (showDataError) {
        setError(showDataError as Error);
      }
    }
    setIsLoading(false);
  }, [getCustomerById, customerId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSubmit = useCallback(
    async (id: number, formValues: Partial<CustomerFormState['values']>) => {
      setIsLoading(true);

      const { data: result } = await updateCustomer({
        variables: {
          id: id,
          input: {
            id: id,
            name: formValues.name || '',
            address: formValues.address || '',
            city: formValues.city || '',
            state: formValues.state || '',
            zip: formValues.zip || '',
            contactId: formValues.contactId || 0
          }
        }
      });

      setIsLoading(false);

      if (result?.updatedCustomer) {
        setCustomer(result.updatedCustomer);
        return true;
      }

      return false;
    },
    [updateCustomer],
  );

  const renderEdit = useMemo(() => {
    if (isLoading) {
      return (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            m: 1,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return (
        <Box sx={{ flexGrow: 1 }}>
          <Alert severity="error">{error.message}</Alert>
        </Box>
      );
    }

    return customer ? (
      <CustomerEditForm initialValues={customer} onSubmit={handleSubmit} />
    ) : null;
  }, [isLoading, error, customer, handleSubmit]);

  return (
    <PageContainer
      title={`Edit ${customer?.name}`}
      breadcrumbs={[
        { title: 'Customers', path: '/dashboard/customers' },
        { title: `${customer?.name}`, path: `/dashboard/customers/${customerId}` },
        { title: 'Edit' },
      ]}
    >
      <Box sx={{ display: 'flex', flex: 1 }}>{renderEdit}</Box>
    </PageContainer>
  );
}
